import { useRef, useCallback } from 'react';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';

import { Iconify } from 'src/components/iconify';
import { TimezoneToggle } from 'src/components/timezone-label-splited/timezone-toggle';
import { useAuthContext } from 'src/auth/hooks';
import { usePopover, CustomPopover } from 'src/components/custom-popover';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
import { ChangePasswordDialog } from './change-password-button';

// ----------------------------------------------------------------------

export function AccountDrawer({ sx, ...other }) {
  const popover = usePopover();

  const changePassword = useBoolean();

  const closeTimer = useRef(null);

  const { user } = useAuthContext();

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const handleOpen = useCallback(
    (event) => {
      cancelClose();
      popover.onOpen(event);
    },
    [cancelClose, popover]
  );

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => popover.onClose(), 150);
  }, [cancelClose, popover]);

  const handleChangePassword = useCallback(() => {
    popover.onClose();
    changePassword.onTrue();
  }, [changePassword, popover]);

  return (
    <>
      <Box
        onClick={handleOpen}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
      >
        <AccountButton
          open={popover.open}
          photoURL={user?.photoURL}
          displayName={user?.name}
          sx={sx}
          {...other}
        />

        {user?.name && (
          <Typography variant="subtitle2" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
            {user.name}
          </Typography>
        )}
      </Box>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: { p: 0, width: 240, pointerEvents: 'auto' },
          },
          arrow: { offset: 20 },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={user?.photoURL} alt={user?.name} sx={{ width: 40, height: 40 }}>
            {user?.name?.charAt(0)?.toUpperCase()}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {user?.name}
            </Typography>
            {user?.roleName && (
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                {user.roleName}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Timezone
          </Typography>
          <TimezoneToggle />
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuList sx={{ p: 1 }}>
          <MenuItem onClick={handleChangePassword} sx={{ borderRadius: 1, gap: 1.5 }}>
            <Iconify icon="solar:lock-password-bold" width={18} />
            Change password
          </MenuItem>
        </MenuList>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <SignOutButton onClose={popover.onClose} size="medium" variant="soft" />
        </Box>
      </CustomPopover>

      <ChangePasswordDialog open={changePassword.value} onClose={changePassword.onFalse} />
    </>
  );
}
